import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  AccountBalanceWallet,
  CreditCard,
  Payment,
  Receipt,
  QrCode,
  VpnKey,
  AccountBalance,
  Store,
  AccountBalanceOutlined,
  Clear as ClearIcon,
} from '@mui/icons-material';
import formaPgtoService, { FormaPgtoSelect as FormaPgtoSelectType } from '../../services/formaPgtoService';

export interface FormaPgtoOption {
  value: string;
  label: string;
  icon: React.ReactElement;
  description?: string;
}

// Mapeamento de ícones por nome
const iconMap: { [key: string]: React.ReactElement } = {
  'AccountBalanceWallet': <AccountBalanceWallet />,
  'CreditCard': <CreditCard />,
  'Payment': <Payment />,
  'Receipt': <Receipt />,
  'QrCode': <QrCode />,
  'VpnKey': <VpnKey />,
  'AccountBalance': <AccountBalance />,
  'AccountBalanceOutlined': <AccountBalanceOutlined />,
  'Store': <Store />,
};


interface FormaPgtoSelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  showDescription?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  allowClear?: boolean;
}

const FormaPgtoSelect: React.FC<FormaPgtoSelectProps> = ({
  value,
  onChange,
  label = 'Forma de Pagamento',
  fullWidth = true,
  size = 'small',
  disabled = false,
  error = false,
  helperText,
  showDescription = false,
  variant = 'outlined',
  allowClear = false
}) => {
  const [formasPgto, setFormasPgto] = useState<FormaPgtoOption[]>([]);
  const [loading, setLoading] = useState(true);

  // Carregar formas de pagamento do backend
  useEffect(() => {
    const loadFormasPgto = async () => {
      try {
        setLoading(true);
        const formas = await formaPgtoService.getFormasParaSelect();
        
        // Converter dados do backend para o formato do componente
        const formasConvertidas: FormaPgtoOption[] = formas.map(forma => ({
          value: forma.value,
          label: forma.label,
          icon: iconMap[forma.icone || 'Payment'] || <Payment />,
          description: forma.label // Usar o label como descrição por enquanto
        }));

        setFormasPgto(formasConvertidas);
      } catch (error) {
        console.error('Erro ao carregar formas de pagamento:', error);
        // Não usar fallback - deixar array vazio
        setFormasPgto([]);
      } finally {
        setLoading(false);
      }
    };

    loadFormasPgto();
  }, []);

  const selectedOption = formasPgto.find(option => option.value === value);

  const handleChange = (event: any) => {
    onChange(event.target.value);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange('');
  };

  return (
    <FormControl 
      fullWidth={fullWidth} 
      size={size} 
      disabled={disabled || loading}
      error={error}
      variant={variant}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        onChange={handleChange}
        label={label}
        endAdornment={allowClear && value ? (
          <InputAdornment position="end">
            <ClearIcon 
              onClick={handleClear}
              sx={{ 
                cursor: 'pointer', 
                fontSize: '20px',
                '&:hover': { color: 'error.main' }
              }}
            />
          </InputAdornment>
        ) : undefined}
        renderValue={(selected) => {
          if (!selected) return '';
          
          const option = formasPgto.find(opt => opt.value === selected);
          if (!option) return selected;

          return (
            <Box display="flex" alignItems="center" gap={1}>
              {option.icon}
              <Typography variant="body2">
                {option.label}
              </Typography>
            </Box>
          );
        }}
      >
        {loading ? (
          <MenuItem disabled>
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <CircularProgress size={20} />
              <Typography variant="body2">Carregando...</Typography>
            </Box>
          </MenuItem>
        ) : formasPgto.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Nenhuma forma de pagamento disponível
            </Typography>
          </MenuItem>
        ) : (
          formasPgto.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                {option.icon}
              </ListItemIcon>
              <ListItemText
                primary={option.label}
                secondary={showDescription ? option.description : undefined}
              />
            </MenuItem>
          ))
        )}
      </Select>
      {helperText && (
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5, ml: 1.5 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default FormaPgtoSelect;
